import { format, formatDistanceToNow, parseISO } from 'date-fns';

/** INR currency formatter (FinMin is India-focused). */
const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

export function formatCurrency(amount: number | string | null | undefined): string {
  if (amount == null || amount === '') return '—';
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(n)) return '—';
  return inrFormatter.format(n);
}

/**
 * Human INR price label from a min/max range, handling one-sided bounds:
 * both unset -> "On request", equal -> exact, min only -> "From ₹x",
 * max only -> "Up to ₹x", else "₹min – ₹max".
 */
export function formatPriceRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  const rupee = (n: number) => `₹${n.toLocaleString('en-IN')}`;
  if (min == null && max == null) return 'On request';
  if (min != null && max != null) {
    return min === max ? rupee(min) : `${rupee(min)} – ${rupee(max)}`;
  }
  if (min != null) return `From ${rupee(min)}`;
  return `Up to ${rupee(max as number)}`;
}

export function formatDate(input: string | Date | null | undefined, pattern = 'dd MMM yyyy'): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? parseISO(input) : input;
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, pattern);
}

export function formatRelative(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? parseISO(input) : input;
  if (Number.isNaN(date.getTime())) return '—';
  return formatDistanceToNow(date, { addSuffix: true });
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}
